import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import * as Icon from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, ImageRun, Header, Footer, PageNumber } from "docx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axios from "../../utils/axiosInstance";

const API = process.env.REACT_APP_API_URL || "";

const RIBBON_TABS = [
  { id: "home", label: "Home", icon: "house-door" },
  { id: "insert", label: "Insert", icon: "plus-square" },
  { id: "layout", label: "Layout", icon: "layout-text-window" },
  { id: "review", label: "Review", icon: "spellcheck" },
  { id: "view", label: "View", icon: "eye" },
  { id: "ai", label: "AI Assistant", icon: "robot" },
];

const FONTS = ["Arial", "Times New Roman", "Calibri", "Cambria", "Georgia", "Verdana", "Tahoma", "Courier New"];
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

const AI_TEMPLATES = [
  { id: "daily", label: "Daily Report" },
  { id: "weekly", label: "Weekly Report" },
  { id: "monthly", label: "Monthly Report" },
  { id: "quarterly", label: "Quarterly Report" },
  { id: "annual", label: "Annual Report" },
  { id: "security", label: "Security Report" },
  { id: "complaint", label: "Citizen Complaint Report" },
  { id: "meeting", label: "Community Meeting Report" },
  { id: "health", label: "Health Report" },
  { id: "education", label: "Education Report" },
  { id: "agriculture", label: "Agriculture Report" },
  { id: "infrastructure", label: "Infrastructure Report" },
  { id: "financial", label: "Financial Report" },
  { id: "project", label: "Project Monitoring Report" },
  { id: "performance", label: "Performance Report" },
];

const TEMPLATE_CONTENT = {
  daily: `<h2>Daily Report</h2><p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p><h3>Activities Completed</h3><ul><li>Activity 1: </li><li>Activity 2: </li></ul><h3>Challenges</h3><p></p><h3>Recommendations</h3><p></p>`,
  weekly: `<h2>Weekly Report</h2><p><strong>Week Ending:</strong> ${new Date().toLocaleDateString()}</p><h3>Summary</h3><p></p><h3>Key Achievements</h3><ol><li></li></ol><h3>Challenges</h3><p></p><h3>Next Week Plan</h3><p></p>`,
  monthly: `<h2>Monthly Report</h2><p><strong>Month:</strong> ${new Date().toLocaleDateString()}</p><h3>Introduction</h3><p></p><h3>Objectives</h3><ul><li></li></ul><h3>Main Content</h3><p></p><h3>Observations</h3><p></p><h3>Recommendations</h3><p></p><h3>Conclusion</h3><p></p><hr/><p><em>Signature Section</em></p>`,
  annual: `<h2>Annual Report</h2><p><strong>Year:</strong> ${new Date().getFullYear()}</p><h3>Executive Summary</h3><p></p><h3>Annual Achievements</h3><ol><li></li></ol><h3>Financial Overview</h3><p></p><h3>Challenges & Solutions</h3><p></p><h3>Future Plans</h3><p></p><h3>Conclusion</h3><p></p>`,
  security: `<h2>Security Report</h2><p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p><h3>Incident Overview</h3><p></p><h3>Location</h3><p></p><h3>Actions Taken</h3><ol><li></li></ol><h3>Recommendations</h3><p></p>`,
  complaint: `<h2>Citizen Complaint Report</h2><p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p><h3>Complainant Details</h3><p>Name: </p><h3>Complaint Description</h3><p></p><h3>Investigation</h3><p></p><h3>Resolution</h3><p></p>`,
  meeting: `<h2>Community Meeting Report</h2><p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p><h3>Meeting Purpose</h3><p></p><h3>Attendance</h3><p></p><h3>Discussion Points</h3><ol><li></li></ol><h3>Decisions Made</h3><p></p><h3>Action Items</h3><p></p>`,
  health: `<h2>Health Report</h2><p><strong>Period:</strong> ${new Date().toLocaleDateString()}</p><h3>Health Indicators</h3><p></p><h3>Services Provided</h3><ul><li></li></ul><h3>Challenges</h3><p></p>`,
  education: `<h2>Education Report</h2><p><strong>Period:</strong> ${new Date().toLocaleDateString()}</p><h3>School Statistics</h3><p></p><h3>Achievements</h3><ul><li></li></ul><h3>Challenges</h3><p></p>`,
  agriculture: `<h2>Agriculture Report</h2><p><strong>Season:</strong> ${new Date().toLocaleDateString()}</p><h3>Crop Status</h3><p></p><h3>Livestock</h3><p></p><h3>Interventions</h3><ul><li></li></ul>`,
  infrastructure: `<h2>Infrastructure Report</h2><p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p><h3>Projects</h3><ul><li></li></ul><h3>Status</h3><p></p><h3>Issues</h3><p></p>`,
  financial: `<h2>Financial Report</h2><p><strong>Period:</strong> ${new Date().toLocaleDateString()}</p><h3>Revenue</h3><p></p><h3>Expenditure</h3><p></p><h3>Balance</h3><p></p>`,
  project: `<h2>Project Monitoring Report</h2><p><strong>Project:</strong> <br/><strong>Date:</strong> ${new Date().toLocaleDateString()}</p><h3>Progress</h3><p></p><h3>Milestones</h3><ul><li></li></ul><h3>Risks</h3><p></p>`,
  performance: `<h2>Performance Report</h2><p><strong>Period:</strong> ${new Date().toLocaleDateString()}</p><h3>KPIs</h3><table border="1" cellpadding="5" style="border-collapse:collapse;width:100%"><tr><th>Indicator</th><th>Target</th><th>Achieved</th></tr><tr><td></td><td></td><td></td></tr></table><h3>Assessment</h3><p></p>`,
  quarterly: `<h2>Quarterly Report</h2><p><strong>Quarter:</strong> Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}</p><h3>Summary</h3><p></p><h3>Achievements</h3><ol><li></li></ol><h3>Challenges</h3><p></p><h3>Next Quarter Plan</h3><p></p>`,
};

function WordEditor({ reportId, onSave, onBack }) {
  const [activeRibbon, setActiveRibbon] = useState("home");
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("Raporo");
  const [fontFamily, setFontFamily] = useState("Calibri");
  const [fontSize, setFontSize] = useState(12);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const [highlightColor, setHighlightColor] = useState("transparent");
  const [alignment, setAlignment] = useState("left");
  const [showAI, setShowAI] = useState(false);
  const [aiTemplate, setAiTemplate] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [viewMode, setViewMode] = useState("print");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showInsertTable, setShowInsertTable] = useState(false);
  const [showShapes, setShowShapes] = useState(false);
  const [showIcons, setShowIcons] = useState(false);
  const [showStamp, setShowStamp] = useState(false);
  const [tableContextMenu, setTableContextMenu] = useState(null);
  const [tablePickerHover, setTablePickerHover] = useState({ rows: 0, cols: 0 });
  const [activeTable, setActiveTable] = useState(null);
  const [activeTableCell, setActiveTableCell] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [paperSize, setPaperSize] = useState("A4");
  const [margins, setMargins] = useState("Normal");
  const [orientation, setOrientation] = useState("Portrait");
  const [columns, setColumns] = useState(1);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (reportId) {
      axios.get(`${API}/api/amaraporo/${reportId}`).then(res => {
        setTitle(res.data.title);
        const html = res.data.content || "";
        setContent(html);
        if (editorRef.current) {
          editorRef.current.innerHTML = html;
        }
      }).catch(() => toast.error("Failed to load report"));
    }
  }, [reportId]);

  useEffect(() => {
    if (!reportId && editorRef.current && !editorRef.current.innerHTML.trim()) {
      editorRef.current.innerHTML = "<p>Start writing your report here...</p>";
    }
  }, [reportId]);

  const exec = useCallback((cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) editorRef.current.focus();
  }, []);

  const handleFontChange = useCallback((e) => {
    const v = e.target.value;
    setFontFamily(v);
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const selectedText = range.extractContents();
    const span = document.createElement("span");
    span.style.fontFamily = v;
    span.appendChild(selectedText);
    range.insertNode(span);
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    sel.addRange(newRange);
    if (editorRef.current) editorRef.current.focus();
  }, []);

  const handleSizeChange = useCallback((e) => {
    const v = e.target.value;
    setFontSize(Number(v));
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const selectedText = range.extractContents();
    const span = document.createElement("span");
    span.style.fontSize = `${v}pt`;
    span.appendChild(selectedText);
    range.insertNode(span);
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    sel.addRange(newRange);
    if (editorRef.current) editorRef.current.focus();
  }, []);

  const toggleBold = useCallback(() => { exec("bold"); setIsBold(p => !p); }, [exec]);
  const toggleItalic = useCallback(() => { exec("italic"); setIsItalic(p => !p); }, [exec]);
  const toggleUnderline = useCallback(() => { exec("underline"); setIsUnderline(p => !p); }, [exec]);

  const handleColorChange = useCallback((color) => {
    setTextColor(color);
    exec("foreColor", color);
    setShowColorPicker(false);
  }, [exec]);

  const handleHighlight = useCallback((color) => {
    setHighlightColor(color);
    exec("hiliteColor", color);
    setShowHighlightPicker(false);
  }, [exec]);

  const setAlign = useCallback((align) => {
    setAlignment(align);
    exec(`justify${align.charAt(0).toUpperCase() + align.slice(1)}`);
  }, [exec]);

  const insertTable = useCallback((rows, cols) => {
    let html = `<div class="word-editor-table-wrapper" style="position:relative;margin:8px 0;overflow:visible">`;
    html += `<table class="word-editor-table" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;font-family:Calibri,Arial,sans-serif;font-size:11pt">`;
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) {
        const isHeader = r === 0;
        const tag = isHeader ? "th" : "td";
        const extraStyle = isHeader
          ? 'background:#f0f0f0;font-weight:600;text-align:left;border-bottom:2px solid #9e9e9e'
          : 'vertical-align:top';
        html += `<${tag} style="border:1px solid #9e9e9e;padding:6px 8px;position:relative;${extraStyle}">`;
        html += `<div class="table-cell-content" style="min-height:1.2em;outline:none">&nbsp;</div>`;
        html += `<div class="col-resize-handle" style="position:absolute;top:0;right:-5px;width:10px;height:100%;cursor:col-resize;z-index:10;background:transparent"></div>`;
        if (r === rows - 1) {
          html += `<div class="row-resize-handle" style="position:absolute;bottom:-5px;left:0;width:100%;height:10px;cursor:row-resize;z-index:10;background:transparent"></div>`;
        }
        html += `</${tag}>`;
      }
      html += "</tr>";
    }
    html += "</table></div><br/>";
    exec("insertHTML", html);
    setShowInsertTable(false);
    setTimeout(() => {
      if (editorRef.current) {
        const tables = editorRef.current.querySelectorAll('table.word-editor-table');
        const lastTable = tables[tables.length - 1];
        if (lastTable) {
          const firstCell = lastTable.querySelector('td, th');
          if (firstCell) {
            const range = document.createRange();
            range.setStart(firstCell, 0);
            range.collapse(true);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      }
    }, 50);
  }, [exec]);

  const insertImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      exec("insertImage", ev.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }, [exec]);

  const insertDate = useCallback(() => {
    exec("insertText", new Date().toLocaleDateString());
  }, [exec]);

  const insertPageBreak = useCallback(() => {
    exec("insertHTML", '<div style="page-break-before:always;clear:both"></div>');
  }, [exec]);

  const insertHeader = useCallback(() => {
    exec("insertHTML", `<div style="text-align:center;border-bottom:2px solid #1e3a8a;padding-bottom:8px;margin-bottom:16px;font-size:10px;color:#666">${title} — ${new Date().toLocaleDateString()}</div>`);
  }, [exec, title]);

  const insertFooter = useCallback(() => {
    exec("insertHTML", `<div style="text-align:center;border-top:2px solid #1e3a8a;padding-top:8px;margin-top:16px;font-size:10px;color:#666">Page | ${new Date().toLocaleDateString()}</div>`);
  }, [exec]);

  const insertBullets = useCallback(() => exec("insertUnorderedList"), [exec]);
  const insertNumbering = useCallback(() => exec("insertOrderedList"), [exec]);
  const indent = useCallback(() => exec("indent"), [exec]);
  const outdent = useCallback(() => exec("outdent"), [exec]);

  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  }, []);

  const handleSpellCheck = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.spellcheck = !editorRef.current.spellcheck;
      toast.info(`Spell check: ${editorRef.current.spellcheck ? "ON" : "OFF"}`);
    }
  }, []);

  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  }, []);

  const generateAIContent = useCallback((templateId) => {
    setAiLoading(true);
    const tpl = AI_TEMPLATES.find(t => t.id === templateId);
    const html = TEMPLATE_CONTENT[templateId] || `<h2>${tpl?.label || "Report"}</h2><p>${new Date().toLocaleDateString()}</p>`;
    setTimeout(() => {
      exec("insertHTML", html);
      setAiLoading(false);
      setShowAI(false);
      setAiTemplate("");
      toast.success(`${tpl?.label || "Report"} template inserted`);
    }, 500);
  }, [exec]);

  const getContentHTML = useCallback(() => {
    return content || (editorRef.current?.innerHTML || "");
  }, [content]);

  const normalizeText = (text) => (text || "").replace(/\u00a0/g, " ");
  const decodeEntities = (text) => (text || "").replace(/&nbsp;/g, " ").replace(/\u00a0/g, " ");

  const htmlToTextWithTables = (html) => {
    const d = document.createElement("div");
    d.innerHTML = html;
    const walk = (node) => {
      if (node.nodeType === 3) return node.textContent;
      if (node.tagName === "TABLE") {
        let out = "\n";
        const rows = node.querySelectorAll("tr");
        rows.forEach((tr, ri) => {
          const cells = [];
          tr.querySelectorAll("td, th").forEach(td => cells.push(walk(td).trim()));
          if (ri === 0) out += cells.join("  |  ") + "\n";
          else out += cells.join("  |  ") + "\n";
          if (ri === 0) out += "-".repeat(Math.max(20, cells.join("  |  ").length)) + "\n";
        });
        return out + "\n";
      }
      if (node.tagName === "BR") return "\n";
      let text = "";
      node.childNodes.forEach(c => { text += walk(c); });
      if (["P", "DIV", "H1", "H2", "H3", "H4", "LI"].includes(node.tagName)) text += "\n";
      return text;
    };
    return decodeEntities(walk(d).replace(/\u00a0/g, " ").replace(/\n{3,}/g, "\n\n").trim());
  };

  const processDocxNodes = (nodes) => {
    const result = [];
    nodes.forEach(node => {
      if (node.nodeType === 3) {
        result.push(new Paragraph({ children: [new TextRun({ text: normalizeText(node.textContent) })] }));
      } else if (node.tagName === "P") {
        const runs = [];
        node.childNodes.forEach(cn => {
          const text = normalizeText(cn.textContent);
          if (cn.nodeType === 3) runs.push(new TextRun({ text }));
          else if (cn.tagName === "STRONG" || cn.tagName === "B") runs.push(new TextRun({ text, bold: true }));
          else if (cn.tagName === "EM" || cn.tagName === "I") runs.push(new TextRun({ text, italics: true }));
          else if (cn.tagName === "U") runs.push(new TextRun({ text, underline: { type: "single" } }));
          else runs.push(new TextRun({ text }));
        });
        result.push(new Paragraph({ children: runs }));
      } else if (["H1", "H2", "H3", "H4"].includes(node.tagName)) {
        const level = parseInt(node.tagName[1]);
        result.push(new Paragraph({
          children: [new TextRun({ text: normalizeText(node.textContent), bold: true, size: 28 + (6 - level) * 4 })],
          heading: level === 1 ? HeadingLevel.HEADING_1 : level === 2 ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3,
        }));
      } else if (node.tagName === "UL" || node.tagName === "OL") {
        node.querySelectorAll("li").forEach(li => {
          result.push(new Paragraph({
            children: [new TextRun({ text: normalizeText(li.textContent) })],
            bullet: { level: 0 },
          }));
        });
      } else if (node.tagName === "TABLE") {
        const rows = [];
        node.querySelectorAll("tr").forEach(tr => {
          const tds = tr.querySelectorAll("td, th");
          const cells = [];
          tds.forEach(td => {
            cells.push(new TableCell({
              width: { size: 100 / tds.length, type: WidthType.PERCENTAGE },
              children: [new Paragraph({ children: [new TextRun({ text: normalizeText(td.textContent) })] })],
            }));
          });
          rows.push(new TableRow({ children: cells }));
        });
        if (rows.length) result.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows }));
      } else {
        if (node.childNodes && node.childNodes.length > 0) {
          result.push(...processDocxNodes(node.childNodes));
        }
      }
    });
    return result;
  };

  const exportAsDOCX = useCallback(async () => {
    try {
      const html = getContentHTML();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const paragraphs = processDocxNodes(tempDiv.childNodes);
      const doc = new Document({
        title: normalizeText(title),
        description: "Raporo",
        styles: { default: { document: { run: { font: fontFamily, size: fontSize * 2 } } } },
        sections: [{
          properties: {},
          headers: { default: new Header({ children: [new Paragraph({ children: [new TextRun({ text: normalizeText(title), size: 18 })] })] }) },
          footers: { default: new Footer({ children: [new Paragraph({ children: [new TextRun({ children: ["Page ", PageNumber.CURRENT] })] })] }) },
          children: paragraphs,
        }],
      });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${title.replace(/\s+/g, "_")}.docx`);
      toast.success("DOCX exported successfully");
    } catch (err) {
      toast.error("Export failed: " + err.message);
    }
  }, [getContentHTML, title, fontFamily, fontSize]);

  const exportAsPDF = useCallback(async () => {
    try {
      const html = getContentHTML();
      const fullHtml = `
        <div style="font-family:Georgia,'Times New Roman',serif;max-width:700px;margin:0 auto;padding:20px;color:#1e293b;">
          <style>
            body{font-family:${fontFamily},serif;font-size:${fontSize}pt;color:#1e293b}
            table{border-collapse:collapse;width:100%;margin:1em 0}
            th,td{border:1px solid #d1d5db;padding:8px 12px;text-align:left}
            th{background:#1d4ed8;color:#fff;font-weight:700}
            tr:nth-child(even){background:#f8fafc}
          </style>
          <h1 style="font-size:22px;font-weight:800;color:#1d4ed8;text-align:center;margin-bottom:4px;">${title}</h1>
          <p style="font-size:10px;color:#6b7280;text-align:center;margin-bottom:16px;">Date: ${new Date().toLocaleDateString()}</p>
          ${html}
        </div>`;
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;top:0;left:0;width:700px;background:#fff;z-index:-9999;opacity:0;pointer-events:none;";
      container.innerHTML = fullHtml;
      document.body.appendChild(container);
      const canvas = await html2canvas(container, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false,
        width: container.scrollWidth, height: container.scrollHeight,
      });
      document.body.removeChild(container);
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", "a4");
      let heightLeft = imgHeight, position = 0;
      const margin = 10, pageHeight = 297;
      pdf.addImage(imgData, "JPEG", margin, position + margin, imgWidth, imgHeight);
      heightLeft -= pageHeight - 2 * margin;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + (pageHeight - 2 * margin);
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", margin, position + margin, imgWidth, imgHeight);
        heightLeft -= pageHeight - 2 * margin;
      }
      pdf.save(`${title.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF exported successfully");
    } catch (err) {
      toast.error("PDF export failed: " + err.message);
    }
  }, [getContentHTML, title, fontFamily, fontSize]);

  const exportAsHTML = useCallback(() => {
    const contentHtml = normalizeText(getContentHTML());
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><style>body{font-family:${fontFamily};font-size:${fontSize}pt;max-width:800px;margin:auto;padding:40px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:8px}</style></head><body>${contentHtml}</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    saveAs(blob, `${title.replace(/\s+/g, "_")}.html`);
    toast.success("HTML exported");
  }, [getContentHTML, title, fontFamily, fontSize]);

  const exportAsTXT = useCallback(() => {
    const text = htmlToTextWithTables(getContentHTML());
    const blob = new Blob([text], { type: "text/plain" });
    saveAs(blob, `${title.replace(/\s+/g, "_")}.txt`);
    toast.success("TXT exported");
  }, [getContentHTML, title]);

  const printDocument = useCallback(() => {
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Print failed: pop-up was blocked. Please allow pop-ups for this site.");
      return;
    }
    w.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
body{font-family:${fontFamily};font-size:${fontSize}pt;max-width:800px;margin:auto;padding:40px;color:#1e293b}
table{border-collapse:collapse;width:100%;margin:1em 0}
th,td{border:1px solid #d1d5db;padding:8px 12px;text-align:left}
th{background-color:#1d4ed8;color:#fff;font-weight:700}
tr:nth-child(even){background:#f8fafc}
@media print{@page{margin:20mm}}
</style></head><body>${getContentHTML()}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  }, [getContentHTML, title, fontFamily, fontSize]);

  const shareDocument = useCallback(async () => {
    try {
      if (navigator.share) {
        const summary = htmlToTextWithTables(getContentHTML()).slice(0, 500);
        const shareData = { title, text: summary };
        if (navigator.canShare) {
          const html = getContentHTML();
          const file = new File([new Blob([html], { type: "text/html" })], `${title.replace(/\s+/g, "_")}.html`, { type: "text/html" });
          if (navigator.canShare({ files: [file] })) shareData.files = [file];
        }
        await navigator.share(shareData);
        return;
      }
      toast.info("Sharing isn't supported in this browser. Downloading instead.");
      exportAsDOCX();
    } catch (err) {
      if (err.name === "AbortError") return;
      toast.error("Share failed: " + err.message);
    }
  }, [getContentHTML, title, exportAsDOCX]);

  const saveReport = useCallback(() => {
    onSave?.({ title, content: getContentHTML(), report_type: "word" });
  }, [title, content, onSave, getContentHTML]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); saveReport(); }
      if ((e.ctrlKey || e.metaKey) && e.key === "b") { e.preventDefault(); toggleBold(); }
      if ((e.ctrlKey || e.metaKey) && e.key === "i") { e.preventDefault(); toggleItalic(); }
      if ((e.ctrlKey || e.metaKey) && e.key === "u") { e.preventDefault(); toggleUnderline(); }
      if ((e.ctrlKey || e.metaKey) && e.key === "p") { e.preventDefault(); printDocument(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [saveReport, toggleBold, toggleItalic, toggleUnderline, printDocument]);

  useEffect(() => {
    const syncFormatState = () => {
      setIsBold(document.queryCommandState("bold"));
      setIsItalic(document.queryCommandState("italic"));
      setIsUnderline(document.queryCommandState("underline"));
    };
    document.addEventListener("selectionchange", syncFormatState);
    return () => document.removeEventListener("selectionchange", syncFormatState);
  }, []);

  const tableOperations = useMemo(() => ({
    insertRowAbove: () => {
      if (!activeTableCell) return;
      const td = activeTableCell.closest('td, th');
      const tr = td?.closest('tr');
      const table = tr?.closest('table.word-editor-table');
      if (!tr || !table) return;
      const newRow = tr.cloneNode(true);
      newRow.querySelectorAll('td, th').forEach(c => { c.innerHTML = '<span class="table-cell-content">&nbsp;</span>'; });
      table.querySelector('tbody')?.insertBefore(newRow, tr) || tr.parentNode.insertBefore(newRow, tr);
      handleContentChange();
    },
    insertRowBelow: () => {
      if (!activeTableCell) return;
      const td = activeTableCell.closest('td, th');
      const tr = td?.closest('tr');
      const table = tr?.closest('table.word-editor-table');
      if (!tr || !table) return;
      const newRow = tr.cloneNode(true);
      newRow.querySelectorAll('td, th').forEach(c => { c.innerHTML = '<span class="table-cell-content">&nbsp;</span>'; });
      tr.parentNode.insertBefore(newRow, tr.nextSibling);
      handleContentChange();
    },
    insertColLeft: () => {
      if (!activeTableCell) return;
      const td = activeTableCell.closest('td, th');
      const tr = td?.closest('tr');
      const table = tr?.closest('table.word-editor-table');
      if (!td || !table) return;
      const cellIndex = Array.from(tr.children).indexOf(td);
      table.querySelectorAll('tr').forEach(row => {
        const newCell = document.createElement(row.children[0]?.tagName || 'td');
        newCell.innerHTML = '<span class="table-cell-content">&nbsp;</span>';
        newCell.style.border = '1px solid #9e9e9e';
        newCell.style.padding = '6px 8px';
        newCell.style.verticalAlign = 'top';
        row.insertBefore(newCell, row.children[cellIndex]);
      });
      handleContentChange();
    },
    insertColRight: () => {
      if (!activeTableCell) return;
      const td = activeTableCell.closest('td, th');
      const tr = td?.closest('tr');
      const table = tr?.closest('table.word-editor-table');
      if (!td || !table) return;
      const cellIndex = Array.from(tr.children).indexOf(td);
      table.querySelectorAll('tr').forEach(row => {
        const newCell = document.createElement(row.children[0]?.tagName || 'td');
        newCell.innerHTML = '<span class="table-cell-content">&nbsp;</span>';
        newCell.style.border = '1px solid #9e9e9e';
        newCell.style.padding = '6px 8px';
        newCell.style.verticalAlign = 'top';
        row.insertBefore(newCell, row.children[cellIndex + 1] || null);
      });
      handleContentChange();
    },
    deleteRow: () => {
      if (!activeTableCell) return;
      const td = activeTableCell.closest('td, th');
      const tr = td?.closest('tr');
      const table = tr?.closest('table.word-editor-table');
      if (!tr || !table || table.querySelectorAll('tr').length <= 1) return;
      tr.remove();
      handleContentChange();
    },
    deleteCol: () => {
      if (!activeTableCell) return;
      const td = activeTableCell.closest('td, th');
      const tr = td?.closest('tr');
      const table = tr?.closest('table.word-editor-table');
      if (!td || !table) return;
      const cellIndex = Array.from(tr.children).indexOf(td);
      let minCells = Infinity;
      table.querySelectorAll('tr').forEach(row => { minCells = Math.min(minCells, row.children.length); });
      if (minCells <= 1) return;
      table.querySelectorAll('tr').forEach(row => {
        if (row.children[cellIndex]) row.children[cellIndex].remove();
      });
      handleContentChange();
    },
    deleteTable: () => {
      if (!activeTableCell) return;
      const td = activeTableCell.closest('td, th');
      const table = td?.closest('table.word-editor-table');
      if (table) { table.remove(); handleContentChange(); }
    },
  }), [activeTableCell, handleContentChange]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleTableContextMenu = (e) => {
      const td = e.target.closest('td, th');
      if (!td || !td.closest('table.word-editor-table')) return;
      e.preventDefault();
      setActiveTableCell(td);
      const table = td.closest('table.word-editor-table');
      setActiveTable(table);
      const rect = td.getBoundingClientRect();
      setTableContextMenu({ x: e.clientX, y: e.clientY, table, cell: td });
    };

    const handleTableClick = (e) => {
      const td = e.target.closest('td, th');
      if (td && td.closest('table.word-editor-table')) {
        setActiveTableCell(td);
        setActiveTable(td.closest('table.word-editor-table'));
      } else {
        setTableContextMenu(null);
      }
    };

    const handleEditorKeyDown = (e) => {
      if (e.key === 'Tab') {
        const td = e.target.closest('td, th');
        if (td && td.closest('table.word-editor-table')) {
          e.preventDefault();
          const table = td.closest('table');
          const allCells = Array.from(table.querySelectorAll('td, th'));
          const idx = allCells.indexOf(td);
          const next = e.shiftKey ? allCells[idx - 1] : allCells[idx + 1];
          if (next) {
            setActiveTableCell(next);
            const range = document.createRange();
            const textNode = next.querySelector('.table-cell-content') || next;
            range.setStart(textNode, 0);
            range.collapse(true);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
            next.focus();
          } else if (!e.shiftKey) {
            const rows = table.querySelectorAll('tr').length;
            const cols = table.querySelector('tr')?.children.length || 1;
            tableOperations.insertRowBelow();
            setTimeout(() => {
              const newRow = table.querySelectorAll('tr')[rows];
              if (newRow) {
                const newCell = newRow.children[0];
                if (newCell) {
                  setActiveTableCell(newCell);
                  const range = document.createRange();
                  range.setStart(newCell, 0);
                  range.collapse(true);
                  window.getSelection().removeAllRanges();
                  window.getSelection().addRange(range);
                }
              }
            }, 50);
          }
        }
      }
    };

    const handleMouseMove = (e) => {
      if (resizing) {
        e.preventDefault();
        const { type, index, table, startX, startY, startWidths } = resizing;
        if (type === 'col') {
          const dx = e.clientX - startX;
          const rows = table.querySelectorAll('tr');
          rows.forEach(row => {
            const cell = row.children[index];
            if (cell) {
              const newWidth = Math.max(30, startWidths[index] + dx);
              cell.style.width = newWidth + 'px';
              cell.style.maxWidth = newWidth + 'px';
            }
          });
        } else if (type === 'row') {
          const dy = e.clientY - startY;
          const row = table.querySelectorAll('tr')[index];
          if (row) {
            const newHeight = Math.max(20, startWidths[0] + dy);
            row.style.height = newHeight + 'px';
            row.querySelectorAll('td, th').forEach(c => { c.style.height = newHeight + 'px'; });
          }
        }
      }
    };

    const handleMouseUp = () => {
      if (resizing) {
        setResizing(null);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    const handleTableResizeStart = (e) => {
      const handle = e.target.closest('.col-resize-handle, .row-resize-handle');
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      const table = handle.closest('table');
      if (handle.classList.contains('col-resize-handle')) {
        const td = handle.closest('td, th');
        const tr = td?.closest('tr');
        const cellIndex = tr ? Array.from(tr.children).indexOf(td) : 0;
        const widths = [];
        table.querySelectorAll('tr').forEach(row => {
          const cell = row.children[cellIndex];
          if (cell) widths.push(cell.offsetWidth);
        });
        setResizing({ type: 'col', index: cellIndex, table, startX: e.clientX, startY: e.clientY, startWidths: widths });
        document.body.style.cursor = 'col-resize';
      } else if (handle.classList.contains('row-resize-handle')) {
        const tr = handle.closest('tr');
        const rowIndex = tr ? Array.from(tr.parentNode.children).indexOf(tr) : 0;
        setResizing({ type: 'row', index: rowIndex, table, startX: e.clientX, startY: e.clientY, startWidths: [tr.offsetHeight] });
        document.body.style.cursor = 'row-resize';
      }
      document.body.style.userSelect = 'none';
    };

    editor.addEventListener('contextmenu', handleTableContextMenu);
    editor.addEventListener('click', handleTableClick);
    editor.addEventListener('keydown', handleEditorKeyDown);
    editor.addEventListener('mousedown', handleTableResizeStart);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      editor.removeEventListener('contextmenu', handleTableContextMenu);
      editor.removeEventListener('click', handleTableClick);
      editor.removeEventListener('keydown', handleEditorKeyDown);
      editor.removeEventListener('mousedown', handleTableResizeStart);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeTableCell, resizing, tableOperations, handleContentChange]);

  const ribbonIcon = (iconName) => {
    const IconComp = Icon[iconName.split("-").map(p => p.charAt(0).toUpperCase() + p.slice(1)).join("")];
    return IconComp ? <IconComp size={14} /> : null;
  };

  return (
    <div className="word-editor" style={{ display:'flex', flexDirection:'column', height:'100%', background:'#f3f3f3' }}>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleImageUpload} />

      {/* Title Bar */}
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 16px', background:'#fff', borderBottom:'1px solid #e0e0e0' }}>
        <button onClick={onBack} style={{ border:'none', background:'none', cursor:'pointer', padding:4, borderRadius:4, color:'#666' }}>
          <Icon.ArrowLeft size={18} />
        </button>
        <input value={title} onChange={e => setTitle(e.target.value)}
          style={{ flex:1, border:'none', fontSize:16, fontWeight:600, outline:'none', background:'transparent', color:'#333' }}
          placeholder="Report Title" />
        <button onClick={saveReport}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 16px', background:'#2563eb', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600 }}>
          <Icon.Save size={15} /> Save
        </button>
        <button onClick={() => setShowExport(true)}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 16px', background:'#059669', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600 }}>
          <Icon.FileEarmarkArrowDown size={15} /> Export
        </button>
      </div>

      {/* Ribbon Tabs */}
      <div style={{ display:'flex', gap:0, background:'#f8f9fa', borderBottom:'1px solid #e0e0e0', padding:'0 8px' }}>
        {RIBBON_TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveRibbon(tab.id)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', border:'none', background: activeRibbon === tab.id ? '#fff' : 'transparent', cursor:'pointer', fontSize:12, fontWeight: activeRibbon === tab.id ? 600 : 400, color: activeRibbon === tab.id ? '#2563eb' : '#666', borderBottom: activeRibbon === tab.id ? '2px solid #2563eb' : '2px solid transparent', transition:'all 0.15s' }}>
            {ribbonIcon(tab.icon)} {tab.label}
          </button>
        ))}
      </div>

      {/* Ribbon Toolbar */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:4, padding:'6px 12px', background:'#fff', borderBottom:'1px solid #e0e0e0', alignItems:'center', minHeight:42, position:'relative' }}>
        {activeRibbon === "home" && (
          <>
            <select value={fontFamily} onChange={handleFontChange} style={selectStyle}>
              {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select value={fontSize} onChange={handleSizeChange} style={{ ...selectStyle, width:60 }}>
              {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style={divider} />
            <ToolBtn active={isBold} onClick={toggleBold} title="Bold (Ctrl+B)"><Icon.TypeBold size={15} /></ToolBtn>
            <ToolBtn active={isItalic} onClick={toggleItalic} title="Italic (Ctrl+I)"><Icon.TypeItalic size={15} /></ToolBtn>
            <ToolBtn active={isUnderline} onClick={toggleUnderline} title="Underline (Ctrl+U)"><Icon.TypeUnderline size={15} /></ToolBtn>
            <div style={divider} />
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setShowColorPicker(p => !p)} title="Text Color"><Icon.Palette size={15} style={{ color: textColor }} /></ToolBtn>
              {showColorPicker && <ColorPicker onPick={handleColorChange} onClose={() => setShowColorPicker(false)} />}
            </div>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setShowHighlightPicker(p => !p)} title="Highlight"><Icon.Highlighter size={15} /></ToolBtn>
              {showHighlightPicker && <ColorPicker onPick={handleHighlight} onClose={() => setShowHighlightPicker(false)} />}
            </div>
            <div style={divider} />
            <ToolBtn onClick={() => setAlign("left")} active={alignment === "left"} title="Align Left"><Icon.TextLeft size={15} /></ToolBtn>
            <ToolBtn onClick={() => setAlign("center")} active={alignment === "center"} title="Center"><Icon.TextCenter size={15} /></ToolBtn>
            <ToolBtn onClick={() => setAlign("right")} active={alignment === "right"} title="Align Right"><Icon.TextRight size={15} /></ToolBtn>
            <div style={divider} />
            <ToolBtn onClick={insertBullets} title="Bullets"><Icon.ListUl size={15} /></ToolBtn>
            <ToolBtn onClick={insertNumbering} title="Numbering"><Icon.ListOl size={15} /></ToolBtn>
            <div style={divider} />
            <ToolBtn onClick={outdent} title="Decrease Indent"><Icon.Indent size={15} /></ToolBtn>
            <ToolBtn onClick={indent} title="Increase Indent"><Icon.TextIndentLeft size={15} /></ToolBtn>
            {activeTable && <div style={divider} />}
            {activeTable && (
              <>
                <ToolBtn onClick={tableOperations.insertRowAbove} title="Insert Row Above"><Icon.FileEarmarkPlus size={15} /></ToolBtn>
                <ToolBtn onClick={tableOperations.insertRowBelow} title="Insert Row Below"><Icon.FileEarmarkPlus size={15} style={{transform:'rotate(180deg)'}} /></ToolBtn>
                <ToolBtn onClick={tableOperations.insertColLeft} title="Insert Column Left"><Icon.FileEarmarkPlus size={15} style={{transform:'rotate(270deg)'}} /></ToolBtn>
                <ToolBtn onClick={tableOperations.insertColRight} title="Insert Column Right"><Icon.FileEarmarkPlus size={15} style={{transform:'rotate(90deg)'}} /></ToolBtn>
                <div style={divider} />
                <ToolBtn onClick={tableOperations.deleteRow} title="Delete Row"><Icon.FileEarmarkMinus size={15} /></ToolBtn>
                <ToolBtn onClick={tableOperations.deleteCol} title="Delete Column"><Icon.FileEarmarkMinus size={15} style={{transform:'rotate(270deg)'}} /></ToolBtn>
                <ToolBtn onClick={tableOperations.deleteTable} title="Delete Table"><Icon.Trash size={15} /></ToolBtn>
              </>
            )}
          </>
        )}
        {activeRibbon === "insert" && (
          <>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setShowInsertTable(p => !p)} title="Table"><Icon.Table size={15} /></ToolBtn>
              {showInsertTable && <TablePicker onInsert={insertTable} onClose={() => setShowInsertTable(false)} />}
            </div>
            <ToolBtn onClick={insertImage} title="Image"><Icon.Image size={15} /></ToolBtn>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setShowShapes(p => !p)} title="Shapes"><Icon.Shield size={15} /></ToolBtn>
              {showShapes && (
                <div style={{ position:'absolute', top:'100%', left:0, zIndex:1000, background:'#fff', border:'1px solid #ddd', borderRadius:8, padding:8, boxShadow:'0 4px 12px rgba(0,0,0,0.15)', display:'flex', gap:4, flexWrap:'wrap', width:160 }}>
                  {["Rectangle","Circle","Arrow","Line","Triangle","Star"].map(s => (
                    <div key={s} onClick={() => { exec("insertHTML", `<div style="width:60px;height:60px;background:#e2e8f0;border:1px solid #94a3b8;display:inline-flex;align-items:center;justify-content:center;margin:4px;font-size:10px;color:#64748b">${s}</div>`); setShowShapes(false); }}
                      style={{ width:60, height:40, display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1px solid #ddd', borderRadius:4, cursor:'pointer', fontSize:10, background:'#fafafa' }}>{s}</div>
                  ))}
                </div>
              )}
            </div>
            <ToolBtn onClick={() => { exec("insertHTML", '😊 👍 ❤️ 🎉 ✅ 📝 📊 📋'); toast.info("Emoji inserted!"); }} title="Icons"><Icon.EmojiSmile size={15} /></ToolBtn>
            <div style={divider} />
            <ToolBtn onClick={insertHeader} title="Header"><Icon.FileText size={15} /></ToolBtn>
            <ToolBtn onClick={insertFooter} title="Footer"><Icon.FileRichtext size={15} /></ToolBtn>
            <ToolBtn onClick={insertDate} title="Date"><Icon.Calendar size={15} /></ToolBtn>
            <ToolBtn onClick={insertPageBreak} title="Page Break"><Icon.FileEarmarkBreak size={15} /></ToolBtn>
            <div style={divider} />
            <ToolBtn onClick={() => toast.info("Signature feature: Place cursor where you want the signature line, then add your signature.")} title="Signature"><Icon.Pen size={15} /></ToolBtn>
            <ToolBtn onClick={() => toast.info("QR Code generation coming soon.")} title="QR Code"><Icon.QrCode size={15} /></ToolBtn>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setShowStamp(p => !p)} title="Stamp"><Icon.PatchCheck size={15} /></ToolBtn>
              {showStamp && (
                <div style={{ position:'absolute', top:'100%', left:0, zIndex:1000, background:'#fff', border:'1px solid #ddd', borderRadius:8, padding:10, boxShadow:'0 4px 12px rgba(0,0,0,0.15)', width:160 }}>
                  <div style={{ fontSize:11, fontWeight:600, marginBottom:4, color:'#333' }}>Insert Stamp</div>
                  {["APPROVED","REJECTED","PENDING","CONFIDENTIAL","DRAFT","FINAL"].map(stamp => (
                    <div key={stamp} onClick={() => { exec("insertHTML", `<span style="display:inline-block;padding:4px 12px;border:2px solid #ef4444;border-radius:4px;color:#ef4444;font-weight:700;font-size:14px;margin:4px;transform:rotate(-15deg)">${stamp}</span>`); setShowStamp(false); }}
                      style={{ padding:'6px 8px', cursor:'pointer', fontSize:12, borderBottom:'1px solid #f0f0f0', color:'#ef4444', fontWeight:700 }}>{stamp}</div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        {activeRibbon === "layout" && (
          <>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setPaperSize(p => p === "A4" ? "Legal" : p === "Legal" ? "Letter" : "A4")} title={`Paper Size: ${paperSize}`}><Icon.FileEarmark size={15} /></ToolBtn>
              <span style={{ fontSize:10, color:'#666', marginLeft:2 }}>{paperSize}</span>
            </div>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setMargins(p => p === "Normal" ? "Narrow" : p === "Narrow" ? "Wide" : "Normal")} title={`Margins: ${margins}`}><Icon.UpcScan size={15} /></ToolBtn>
              <span style={{ fontSize:10, color:'#666', marginLeft:2 }}>{margins}</span>
            </div>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setOrientation(p => p === "Portrait" ? "Landscape" : "Portrait")} title={`Orientation: ${orientation}`}><Icon.ArrowLeftRight size={15} /></ToolBtn>
              <span style={{ fontSize:10, color:'#666', marginLeft:2 }}>{orientation}</span>
            </div>
            <div style={{ position:'relative' }}>
              <ToolBtn onClick={() => setColumns(p => p >= 3 ? 1 : p + 1)} title={`Columns: ${columns}`}><Icon.ColumnsGap size={15} /></ToolBtn>
              <span style={{ fontSize:10, color:'#666', marginLeft:2 }}>{columns} col</span>
            </div>
            <div style={divider} />
            <ToolBtn onClick={insertPageBreak} title="Page Break"><Icon.FileEarmarkBreak size={15} /></ToolBtn>
          </>
        )}
        {activeRibbon === "review" && (
          <>
            <ToolBtn onClick={handleSpellCheck} title="Spell Check"><Icon.Check2Square size={15} /></ToolBtn>
            <ToolBtn onClick={() => toast.info("Comments: Click anywhere in the document to add a comment.", { autoClose: 3000 })} title="Comments"><Icon.ChatSquareText size={15} /></ToolBtn>
            <ToolBtn onClick={() => toast.info("Track Changes: Changes are being tracked automatically via contentEditable.")} title="Track Changes"><Icon.PencilSquare size={15} /></ToolBtn>
            <div style={divider} />
            <ToolBtn onClick={() => exec("removeFormat")} title="Clear Formatting"><Icon.Eraser size={15} /></ToolBtn>
            <ToolBtn onClick={() => { exec("insertHTML", `<span style="background:#fef9c3;padding:0 4px;border-radius:2px">${window.getSelection().toString()}</span>`); }} title="Highlight Selection"><Icon.Highlighter size={15} /></ToolBtn>
          </>
        )}
        {activeRibbon === "view" && (
          <>
            <ToolBtn onClick={() => setViewMode("print")} active={viewMode === "print"} title="Print Layout"><Icon.FileEarmarkText size={15} /></ToolBtn>
            <ToolBtn onClick={() => setViewMode(p => p === "reading" ? "print" : "reading")} active={viewMode === "reading"} title="Reading Mode"><Icon.Book size={15} /></ToolBtn>
            <div style={divider} />
            <span style={{ fontSize:12, color:'#666' }}>Zoom:</span>
            <input type="range" min={50} max={200} value={zoom} onChange={e => setZoom(Number(e.target.value))} style={{ width:100 }} />
            <span style={{ fontSize:12, color:'#666', minWidth:36 }}>{zoom}%</span>
            <div style={divider} />
            <ToolBtn onClick={() => setZoom(z => Math.min(200, z + 10))} title="Zoom In"><Icon.ZoomIn size={15} /></ToolBtn>
            <ToolBtn onClick={() => setZoom(z => Math.max(50, z - 10))} title="Zoom Out"><Icon.ZoomOut size={15} /></ToolBtn>
            <ToolBtn onClick={() => setZoom(100)} title="Reset Zoom (100%)"><Icon.ArrowCounterclockwise size={15} /></ToolBtn>
            <div style={divider} />
            <ToolBtn onClick={toggleFullScreen} title="Full Screen"><Icon.Fullscreen size={15} /></ToolBtn>
          </>
        )}
        {activeRibbon === "ai" && (
          <>
            <button onClick={() => setShowAI(p => !p)}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 14px', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600 }}>
              <Icon.Robot size={15} /> AI Report Generator
            </button>
            {showAI && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginLeft:8 }}>
                {AI_TEMPLATES.map(tpl => (
                  <button key={tpl.id} onClick={() => generateAIContent(tpl.id)} disabled={aiLoading}
                    style={{ padding:'4px 10px', border:'1px solid #ddd', borderRadius:4, background:'#fff', cursor:'pointer', fontSize:11, color:'#333' }}>
                    {aiLoading ? "..." : tpl.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Editor Area */}
      <div style={{ flex:1, overflow:'auto', padding: viewMode === "reading" ? '40px 80px' : '24px', background: viewMode === "reading" ? '#f0ebe3' : '#e8e8e8' }}>
        <div ref={editorRef} contentEditable suppressContentEditableWarning
          onInput={handleContentChange}
          style={{ maxWidth:794, margin:'0 auto', minHeight: viewMode === "reading" ? 'auto' : 900, padding:'40px 60px', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.12)', fontFamily, fontSize: `${fontSize}pt`, lineHeight:1.6, outline:'none', borderRadius: viewMode === "reading" ? 0 : 2, color:'#333', transform:`scale(${zoom/100})`, transformOrigin:'top center', columnCount: viewMode === "reading" ? columns : 1 }}
          />
        {tableContextMenu && (
          <TableContextMenu
            x={tableContextMenu.x}
            y={tableContextMenu.y}
            onInsertRowAbove={tableOperations.insertRowAbove}
            onInsertRowBelow={tableOperations.insertRowBelow}
            onInsertColLeft={tableOperations.insertColLeft}
            onInsertColRight={tableOperations.insertColRight}
            onDeleteRow={tableOperations.deleteRow}
            onDeleteCol={tableOperations.deleteCol}
            onDeleteTable={tableOperations.deleteTable}
            onClose={() => { setTableContextMenu(null); setActiveTableCell(null); }}
          />
        )}
      </div>

      {/* Status Bar */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'4px 16px', background:'#fff', borderTop:'1px solid #e0e0e0', fontSize:11, color:'#666' }}>
        <span>{viewMode === "reading" ? "Reading View" : "Print Layout"}</span>
        <span>|</span>
        <span>Words: {content.replace(/<[^>]*>/g, "").split(/\s+/).filter(Boolean).length}</span>
        <span>|</span>
        <span>Zoom: {zoom}%</span>
        <div style={{ flex:1 }} />
        <span>{fontFamily} | {fontSize}pt</span>
      </div>

      {/* Export Dialog */}
      {showExport && (
        <ExportModal
          type="word"
          onExportDOCX={exportAsDOCX}
          onExportPDF={exportAsPDF}
          onExportHTML={exportAsHTML}
          onExportTXT={exportAsTXT}
          onPrint={printDocument}
          onShare={shareDocument}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}

function TableContextMenu({ x, y, onInsertRowAbove, onInsertRowBelow, onInsertColLeft, onInsertColRight, onDeleteRow, onDeleteCol, onDeleteTable, onClose }) {
  const menuRef = useRef(null);
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);
  return (
    <div ref={menuRef} className="word-table-context-menu" style={{ left: x, top: y }}>
      <div className="menu-item" onClick={() => { onInsertRowAbove(); onClose(); }}><Icon.FileEarmarkPlus size={14} /> Insert Row Above</div>
      <div className="menu-item" onClick={() => { onInsertRowBelow(); onClose(); }}><Icon.FileEarmarkPlus size={14} /> Insert Row Below</div>
      <div className="menu-item" onClick={() => { onInsertColLeft(); onClose(); }}><Icon.FileEarmarkPlus size={14} /> Insert Column Left</div>
      <div className="menu-item" onClick={() => { onInsertColRight(); onClose(); }}><Icon.FileEarmarkPlus size={14} /> Insert Column Right</div>
      <div className="menu-divider" />
      <div className="menu-item" onClick={() => { onDeleteRow(); onClose(); }}><Icon.FileEarmarkMinus size={14} /> Delete Row</div>
      <div className="menu-item" onClick={() => { onDeleteCol(); onClose(); }}><Icon.FileEarmarkMinus size={14} /> Delete Column</div>
      <div className="menu-divider" />
      <div className="menu-item" onClick={() => { onDeleteTable(); onClose(); }}><Icon.Trash size={14} /> Delete Table</div>
    </div>
  );
}

const ToolBtn = ({ active, onClick, title, children }) => (
  <button onClick={onClick} title={title}
    style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, border: active ? '1px solid #cce4f7' : '1px solid transparent', borderRadius:4, background: active ? '#e8f4fd' : 'transparent', cursor:'pointer', color: active ? '#2563eb' : '#444', transition:'all 0.1s' }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f0f0f0'; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
    {children}
  </button>
);

const selectStyle = { padding:'3px 6px', border:'1px solid #ddd', borderRadius:4, fontSize:12, background:'#fff', color:'#333', cursor:'pointer', outline:'none' };
const divider = { width:1, height:20, background:'#e0e0e0', margin:'0 3px' };

const ColorPicker = ({ onPick, onClose }) => {
  const colors = ["#000000","#434343","#666666","#999999","#b7b7b7","#cccccc","#d9d9d9","#efefef","#f3f3f3","#ffffff",
    "#980000","#ff0000","#ff9900","#ffff00","#00ff00","#00ffff","#4a86e8","#0000ff","#9900ff","#ff00ff",
    "#e6b8af","#f4cccc","#fce5cd","#fff2cc","#d9ead3","#d0e0e3","#c9daf8","#cfe2f3","#d9d2e9","#ead1dc",
    "#dd7e6b","#ea9999","#f9cb9c","#ffe599","#b6d7a8","#a2c4c9","#a4c2f4","#9fc5e8","#b4a7d6","#d5a6bd",
    "#cc4125","#e06666","#f6b26b","#ffd966","#93c47d","#76a5af","#6d9eeb","#6fa8dc","#8e7cc3","#c27ba0"];
  return (
    <div style={{ position:'absolute', top:'100%', left:0, zIndex:1000, background:'#fff', border:'1px solid #ddd', borderRadius:8, padding:8, boxShadow:'0 4px 12px rgba(0,0,0,0.15)', width:200 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(10, 1fr)', gap:2 }}>
        {colors.map(c => (
          <div key={c} onClick={() => onPick(c)} style={{ width:16, height:16, background:c, borderRadius:2, cursor:'pointer', border:'1px solid #e0e0e0' }} />
        ))}
      </div>
      <button onClick={onClose} style={{ marginTop:6, padding:'3px 10px', border:'1px solid #ddd', borderRadius:4, background:'#f5f5f5', cursor:'pointer', fontSize:11, width:'100%' }}>Close</button>
    </div>
  );
};

function TablePicker({ onInsert, onClose }) {
  const [hover, setHover] = useState({ rows: 0, cols: 0 });
  const maxRows = 10;
  const maxCols = 10;
  return (
    <div style={{ position:'absolute', top:'100%', left:0, zIndex:1000, background:'#fff', border:'1px solid #d0d0d0', borderRadius:6, padding:'10px 12px', boxShadow:'0 4px 16px rgba(0,0,0,0.15)', fontFamily:'Segoe UI, Arial, sans-serif' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#333', marginBottom:6 }}>Insert Table</div>
      <div className="table-picker-grid" style={{ gridTemplateColumns:`repeat(${maxCols}, 18px)` }}>
        {Array.from({ length: maxRows * maxCols }, (_, i) => {
          const row = Math.floor(i / maxCols) + 1;
          const col = (i % maxCols) + 1;
          const isHighlighted = hover.rows > 0 && row <= hover.rows && col <= hover.cols;
          const isHovered = row <= hover.rows && col <= hover.cols;
          return (
            <div
              key={i}
              className={`table-picker-cell${isHovered && isHighlighted ? ' highlighted' : ''}${isHovered && hover.rows > 0 ? ' selected' : ''}`}
              onClick={() => onInsert(row, col)}
              onMouseEnter={() => setHover({ rows: row, cols: col })}
            />
          );
        })}
      </div>
      <div style={{ fontSize:11, color:'#555', marginTop:4, textAlign:'center', fontWeight:500 }}>
        {hover.rows > 0 ? `${hover.rows} × ${hover.cols} table` : 'Move mouse to select size'}
      </div>
      <div style={{ borderTop:'1px solid #e8e8e8', marginTop:8, paddingTop:6 }}>
        <button onClick={() => {
          const rows = prompt('Number of rows:', '5');
          if (rows === null) return;
          const cols = prompt('Number of columns:', '4');
          if (cols === null) return;
          const r = parseInt(rows);
          const c = parseInt(cols);
          if (r > 0 && c > 0) onInsert(r, c);
        }}
          style={{ width:'100%', padding:'5px 8px', border:'1px solid #d0d0d0', borderRadius:4, background:'#fafafa', cursor:'pointer', fontSize:11, color:'#333', textAlign:'left', display:'flex', alignItems:'center', gap:6 }}>
          <Icon.Table size={13} /> Insert Table...
        </button>
      </div>
    </div>
  );
}

const ExportModal = ({ type, onExportDOCX, onExportPDF, onExportHTML, onExportTXT, onExportXLSX, onExportCSV, onPrint, onShare, onClose }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000 }}
    onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:24, width:480, maxWidth:'90vw', maxHeight:'80vh', overflow:'auto' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:'#333' }}>📤 Export Document</h3>
        <button onClick={onClose} style={{ border:'none', background:'none', cursor:'pointer', fontSize:20, color:'#999' }}>✕</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        {type === "word" && (
          <>
            <ExportBtn icon="FileWord" label="DOCX (.docx)" desc="Microsoft Word" color="#2b579a" onClick={onExportDOCX} />
            <ExportBtn icon="FiletypePdf" label="PDF (.pdf)" desc="Adobe PDF" color="#d32f2f" onClick={onExportPDF} />
            <ExportBtn icon="FiletypeHtml" label="HTML (.html)" desc="Web Page" color="#e65100" onClick={onExportHTML} />
            <ExportBtn icon="FiletypeTxt" label="TXT (.txt)" desc="Plain Text" color="#555" onClick={onExportTXT} />
          </>
        )}
        {type === "excel" && (
          <>
            <ExportBtn icon="FileExcel" label="XLSX (.xlsx)" desc="Excel Workbook" color="#217346" onClick={onExportXLSX} />
            <ExportBtn icon="FiletypeCsv" label="CSV (.csv)" desc="CSV File" color="#0d6efd" onClick={onExportCSV} />
            <ExportBtn icon="FiletypePdf" label="PDF (.pdf)" desc="Adobe PDF" color="#d32f2f" onClick={onExportPDF} />
            <ExportBtn icon="FiletypeHtml" label="HTML (.html)" desc="Web Page" color="#e65100" onClick={onExportHTML} />
          </>
        )}
      </div>
      <div style={{ borderTop:'1px solid #eee', marginTop:16, paddingTop:16 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
          <ActionBtn icon="Printer" label="Print" color="#333" onClick={onPrint} />
          <ActionBtn icon="Share" label="Share" color="#2563eb" onClick={onShare} />
          <ActionBtn icon="Download" label="Download" color="#059669" onClick={type === "word" ? onExportDOCX : onExportXLSX} />
        </div>
      </div>
    </div>
  </div>
);

const ExportBtn = ({ icon, label, desc, color, onClick }) => (
  <button onClick={onClick}
    style={{ display:'flex', alignItems:'center', gap:10, padding:12, border:'1px solid #e0e0e0', borderRadius:10, background:'#fff', cursor:'pointer', transition:'all 0.15s', textAlign:'left' }}
    onMouseEnter={e => e.currentTarget.style.borderColor = color}
    onMouseLeave={e => e.currentTarget.style.borderColor = '#e0e0e0'}>
    <div style={{ width:36, height:36, borderRadius:8, background: `${color}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      {React.createElement(Icon[icon] || Icon.FileEarmark, { size: 18, color })}
    </div>
    <div style={{ minWidth:0 }}>
      <div style={{ fontSize:13, fontWeight:600, color:'#333' }}>{label}</div>
      <div style={{ fontSize:10, color:'#999' }}>{desc}</div>
    </div>
  </button>
);

const ActionBtn = ({ icon, label, color, onClick }) => (
  <button onClick={onClick}
    style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:'10px 8px', border:'1px solid #e0e0e0', borderRadius:10, background:'#fff', cursor:'pointer', transition:'all 0.15s', fontSize:12, fontWeight:600, color:'#333' }}
    onMouseEnter={e => e.currentTarget.style.borderColor = color}
    onMouseLeave={e => e.currentTarget.style.borderColor = '#e0e0e0'}>
    {React.createElement(Icon[icon] || Icon.FileEarmark, { size: 20, color })}
    {label}
  </button>
);

export default WordEditor;
